import { useState } from 'react';
import { InformationCircle } from 'assets/icons';
import useText from 'hooks/useText';

// « +596596106401 » → « +596 596 10 64 01 », the way the screens write numbers.
const formatNumber = (number) => {
  const match = /^\+(\d{3})(\d{3})(\d{2})(\d{2})(\d{2})$/.exec(number || '');
  return match ? `+${match.slice(1).join(' ')}` : number || '';
};

/**
 * Beside « À appeler »: the agency number to show the prospects of the list.
 * `lines`: the agencies and their Wildix numbers ({ agency, number }, from
 * phone/caller-lines; none, nothing is shown); `calls`: the lines to call,
 * each company carrying its `callerAgency`. Jobbiz does not switch the
 * caller's Wildix service yet: the help says to pick it in Collaboration. Opens on hover or focus; a
 * click keeps it open, as OrderHelp on the home page.
 */
const CallerLineHelp = ({ lines = [], calls = [] }) => {
  const { text, plural } = useText('prospection');
  const [open, setOpen] = useState(false);
  if (!lines.length) return null;

  const countOf = (agency) =>
    calls.filter((line) => (line.company?.callerAgency || 'martinique') === agency).length;
  const used = lines.filter((line) => countOf(line.agency) > 0);
  const label =
    used.length > 1
      ? text('callerLine.several', { count: used.length })
      : text(`callerLine.agency.${(used[0] || lines[0]).agency}`);

  return (
    <span
      className="relative flex font-crm"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="flex items-center gap-1 text-xs font-medium text-muted underline-offset-2 hover:underline"
      >
        <InformationCircle wh={15} color="currentColor" />
        {label}
      </button>
      {open && (
        <span className="absolute left-0 top-full z-20 w-96 pt-2 motion-safe:animate-contentIn">
          <span className="block rounded-xl border border-gray-100 bg-white p-4 shadow-lg">
            <span className="block text-sm font-semibold text-gray-900">{text('callerLine.title')}</span>
            <span className="mt-3 flex flex-col gap-3">
              {lines.map((line) => (
                <span key={line.agency} className="flex items-start justify-between gap-3">
                  <span className="flex flex-col">
                    <span className="text-sm font-medium leading-5 text-gray-900">
                      {text(`callerLine.agency.${line.agency}`)}
                    </span>
                    <span className="text-sm leading-5 text-gray-600">
                      {formatNumber(line.number) || text('callerLine.unknownNumber')}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm leading-5 text-gray-600">
                    {plural('callerLine.calls', countOf(line.agency))}
                  </span>
                </span>
              ))}
            </span>
            <span className="mt-4 block border-t border-gray-100 pt-3 text-sm leading-5 text-gray-600">
              {text('callerLine.rule')}
            </span>
            <span className="mt-3 block rounded-lg bg-tagOrange-bg px-3 py-2 text-sm leading-5 text-tagOrange-fg">
              {text('callerLine.manual')}
            </span>
          </span>
        </span>
      )}
    </span>
  );
};

export default CallerLineHelp;
